import { useRef, useState } from 'react'
import { useReactFlow, useViewport } from '@xyflow/react'
import type { Rect } from './frames'

/**
 * Scaling several things at once: four corner handles round a selection.
 * -------------------------------------------------------------
 * React Flow resizes one node at a time and has nothing for a selection, so
 * this is the board's own. Shown when two or more things are selected; one
 * picked alone keeps its own resize handles.
 *
 * Always in proportion (Huanrui, 2026-09-30): a photograph is never
 * stretched, and the whole selection keeps its arrangement. The corner
 * opposite the one being dragged stays where it is. What each kind of thing
 * does is the board's business (ProjectCanvas, scaleGroup): pictures and
 * frames grow, a frame's pictures with it; a skill card is a form and only
 * moves.
 *
 * Drawn on the screen rather than on the board, so the handles are the same
 * size at any zoom -- and in a component of its own, so that following the
 * board as it pans re-draws four squares and not the whole canvas.
 *
 * THE SIDES SPACE THINGS OUT (Huanrui, 2026-11-02)
 *
 * The four sides themselves can be dragged, with no handle drawn: left and
 * right change the gaps across, top and bottom the gaps between rows, and
 * nothing changes size (ProjectCanvas, spaceGroup). What can be taken hold of
 * is a strip just outside the line, so a picture's own edge, which lies on
 * the line, is still the picture's. The corners are left to the squares, and
 * so is wherever a selected picture's own corner handle sits on the line.
 */

type Corner = 'tl' | 'tr' | 'bl' | 'br'
export type Side = 'l' | 'r' | 't' | 'b'

/** How far outside the line the side can be taken hold of, in screen pixels. */
const SIDE_GRIP = 8
/** Kept clear at each end of a side for the corner squares. */
const CORNER_ROOM = 18
/** Kept clear round a selected picture's own corner handle lying on a side. */
const HANDLE_ROOM = 9

interface GroupScaleProps {
  /** Round everything selected and everything inside a selected frame. */
  bounds: Rect
  onBegin: (anchor: { x: number; y: number }) => void
  onScale: (factor: number) => void
  onEnd: () => void
  /** The boxes of the things selected, whose own handles keep their place on a side. */
  boxes: Rect[]
  onSpaceBegin: (side: Side) => void
  /** Where the pointer is on the board while a side is dragged. */
  onSpace: (at: { x: number; y: number }) => void
  onSpaceEnd: () => void
}

export function GroupScale({
  bounds,
  onBegin,
  onScale,
  onEnd,
  boxes,
  onSpaceBegin,
  onSpace,
  onSpaceEnd,
}: GroupScaleProps) {
  const { x: panX, y: panY, zoom } = useViewport()
  const { screenToFlowPosition } = useReactFlow()
  const drag = useRef<{ anchor: { x: number; y: number }; w: number; h: number; sx: number; sy: number } | null>(
    null,
  )

  const left = bounds.x * zoom + panX
  const top = bounds.y * zoom + panY
  const width = bounds.w * zoom
  const height = bounds.h * zoom

  const corners: { at: Corner; x: number; y: number }[] = [
    { at: 'tl', x: 0, y: 0 },
    { at: 'tr', x: width, y: 0 },
    { at: 'bl', x: 0, y: height },
    { at: 'br', x: width, y: height },
  ]

  const start = (corner: Corner, event: React.PointerEvent) => {
    if (event.button !== 0) return
    event.stopPropagation()
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    // The corner across from the one taken hold of.
    const anchor = {
      x: corner === 'tl' || corner === 'bl' ? bounds.x + bounds.w : bounds.x,
      y: corner === 'tl' || corner === 'tr' ? bounds.y + bounds.h : bounds.y,
    }
    drag.current = {
      anchor,
      w: Math.max(1, bounds.w),
      h: Math.max(1, bounds.h),
      sx: corner === 'tl' || corner === 'bl' ? -1 : 1,
      sy: corner === 'tl' || corner === 'tr' ? -1 : 1,
    }
    onBegin(anchor)
  }

  const move = (event: React.PointerEvent) => {
    const held = drag.current
    if (!held) return
    const at = screenToFlowPosition({ x: event.clientX, y: event.clientY })
    // How far the corner has gone along each side, as a share of that side;
    // the larger of the two decides, so the corner follows the pointer.
    const byX = (held.sx * (at.x - held.anchor.x)) / held.w
    const byY = (held.sy * (at.y - held.anchor.y)) / held.h
    onScale(Math.max(0.05, byX, byY))
  }

  const end = (event: React.PointerEvent) => {
    if (!drag.current) return
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    onEnd()
  }

  /* ---------- the sides ---------- */

  /** The side under the pointer, lit; kept lit while it is being dragged. */
  const [hot, setHot] = useState<Side | null>(null)
  const sliding = useRef<Side | null>(null)

  // Followed on the window, not by capturing the pointer on the strip: the
  // strips are re-cut as things move (a picture's corner arriving on the
  // side), and a strip taken away mid-drag would drop the capture with it.
  const live = useRef({ onSpace, onSpaceEnd, screenToFlowPosition })
  live.current = { onSpace, onSpaceEnd, screenToFlowPosition }

  const startSide = (side: Side, event: React.PointerEvent) => {
    if (event.button !== 0) return
    event.stopPropagation()
    event.preventDefault()
    sliding.current = side
    setHot(side)
    onSpaceBegin(side)
    const pointerId = event.pointerId
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pointerId || !sliding.current) return
      live.current.onSpace(live.current.screenToFlowPosition({ x: e.clientX, y: e.clientY }))
    }
    const stop = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
      if (!sliding.current) return
      sliding.current = null
      setHot(null)
      live.current.onSpaceEnd()
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
  }

  /**
   * Each side as the stretches that can be taken hold of: the whole of it but
   * its ends, and a little round each selected thing's corner lying on it.
   */
  const stretches = (side: Side): { from: number; to: number }[] => {
    const across = side === 'l' || side === 'r'
    const length = across ? height : width
    const line = side === 'l' ? 0 : side === 'r' ? width : side === 't' ? 0 : height
    const holes: number[] = []
    for (const box of boxes) {
      const xs = [box.x * zoom + panX - left, (box.x + box.w) * zoom + panX - left]
      const ys = [box.y * zoom + panY - top, (box.y + box.h) * zoom + panY - top]
      for (const x of xs) {
        for (const y of ys) {
          const on = across ? Math.abs(x - line) < 3 : Math.abs(y - line) < 3
          if (on) holes.push(across ? y : x)
        }
      }
    }
    holes.sort((a, b) => a - b)
    const out: { from: number; to: number }[] = []
    let from = CORNER_ROOM
    for (const hole of holes) {
      const to = hole - HANDLE_ROOM
      if (to > from) out.push({ from, to })
      from = Math.max(from, hole + HANDLE_ROOM)
    }
    if (length - CORNER_ROOM > from) out.push({ from, to: length - CORNER_ROOM })
    return out
  }

  const sides: Side[] = ['l', 'r', 't', 'b']

  return (
    <div className="cgroup" style={{ left, top, width, height }} aria-hidden="true">
      {corners.map((corner) => (
        <span
          key={corner.at}
          className={`cgroup__handle cgroup__handle--${corner.at} nodrag nopan`}
          style={{ left: corner.x, top: corner.y }}
          onPointerDown={(event) => start(corner.at, event)}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
      ))}
      {hot ? <span className={`cgroup__lit cgroup__lit--${hot}`} /> : null}
      {sides.map((side) =>
        stretches(side).map(({ from, to }) => {
          const across = side === 'l' || side === 'r'
          const style: React.CSSProperties = across
            ? { left: side === 'l' ? -SIDE_GRIP : width, top: from, width: SIDE_GRIP, height: to - from }
            : { top: side === 't' ? -SIDE_GRIP : height, left: from, height: SIDE_GRIP, width: to - from }
          return (
            <span
              key={`${side}${from}`}
              className={`cgroup__side cgroup__side--${across ? 'across' : 'down'} nodrag nopan`}
              style={style}
              onPointerEnter={() => setHot((was) => was ?? side)}
              onPointerLeave={() => {
                if (!sliding.current) setHot(null)
              }}
              onPointerDown={(event) => startSide(side, event)}
            />
          )
        }),
      )}
    </div>
  )
}
